import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Bell,
  Calendar,
  ChevronRight,
  ClipboardList,
  LayoutDashboard,
  LogOut,
  Menu,
  Settings,
  User,
  Users,
  Activity,
  Clock,
  Shield,
  Stethoscope,
  MessageSquare,
} from "lucide-react";
import axios from "axios";
import { NotificationSettings } from "@/components/NotificationSettings";
import { HelpPanel } from "@/components/HelpPanel";
import { VersionBadge } from "@/components/VersionBadge";
import { WhatsNewCard } from "@/components/WhatsNewCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Build navigation items based on user's roles array
const getNavItemsForRoles = (roles) => {
  if (!roles || roles.length === 0) return [];
  
  const items = [];
  const addedPaths = new Set();
  
  // Add items based on roles (order matters for display)
  if (roles.includes("therapist")) {
    if (!addedPaths.has("/therapist")) {
      items.push({ icon: Stethoscope, label: "Therapist Dashboard", path: "/therapist" });
      addedPaths.add("/therapist");
    }
  }
  
  if (roles.includes("admin")) {
    if (!addedPaths.has("/admin")) {
      items.push({ icon: Shield, label: "Admin Panel", path: "/admin" });
      addedPaths.add("/admin");
    }
    // Admin can also view therapist dashboard even without therapist role
    if (!addedPaths.has("/therapist")) {
      items.push({ icon: Stethoscope, label: "Therapist View", path: "/therapist" });
      addedPaths.add("/therapist");
    }
  }
  
  if (roles.includes("facilitator")) {
    if (!addedPaths.has("/facilitator")) {
      items.push({ icon: LayoutDashboard, label: "Facilitator Dashboard", path: "/facilitator" });
      addedPaths.add("/facilitator");
    }
  }
  
  return items;
};

export const Layout = ({ children }) => {
  const { user, logout, token } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationSettingsOpen, setNotificationSettingsOpen] = useState(false);

  const navItems = getNavItemsForRoles(user?.roles || []);

  // Heartbeat for therapists - runs on ANY page, not just therapist dashboard
  useEffect(() => {
    // Only run heartbeat if user has therapist role
    if (!user?.roles?.includes("therapist")) return;

    const sendHeartbeat = async () => {
      try {
        await axios.post(`${API}/therapists/heartbeat`);
      } catch (error) {
        console.log("Heartbeat failed:", error);
      }
    };

    // Send initial heartbeat
    sendHeartbeat();

    // Send heartbeat every 30 seconds
    const interval = setInterval(sendHeartbeat, 30000);

    // Send offline signal when leaving
    const handleBeforeUnload = () => {
      navigator.sendBeacon(`${API}/therapists/offline`, JSON.stringify({}));
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Send offline signal on unmount
      axios.post(`${API}/therapists/offline`).catch(() => {});
    };
  }, [user?.roles]);

  const handleLogout = () => {
    // Send offline signal before logout
    if (user?.roles?.includes("therapist")) {
      axios.post(`${API}/therapists/offline`).catch(() => {});
    }
    logout();
    navigate("/login");
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/40">
        <div className="flex h-20 items-center px-4 md:px-8">
          {/* Mobile menu button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <div className="flex flex-col h-full">
                <div className="p-6 border-b">
                  <Link to="/" className="flex items-center gap-3">
                    <div className="flex items-center gap-3">
                      <img src="/tmc-logo.png" alt="TMC TeleTech" className="h-32 w-auto" />
                      <span className="font-bold text-2xl font-['Manrope']">TMC TeleTech</span>
                    </div>
                  </Link>
                </div>
                <nav className="flex-1 p-4">
                  {navItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                        location.pathname === item.path
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-secondary"
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </nav>
              </div>
            </SheetContent>
          </Sheet>

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mr-8">
            <div className="flex items-center gap-3">
              <img src="/tmc-logo.png" alt="TMC TeleTech" className="h-28 w-auto" />
              <span className="font-bold text-2xl font-['Manrope'] hidden sm:block">TMC TeleTech</span>
              <VersionBadge className="hidden sm:inline-block ml-1" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  location.pathname === item.path
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-secondary text-foreground"
                }`}
              >
                <item.icon className="w-4 h-4" />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            {/* Help Panel */}
            <HelpPanel />

            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-accent text-accent-foreground text-xs">
                      {unreadCount}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="flex items-center justify-between px-4 py-2 border-b">
                  <span className="font-semibold">Notifications</span>
                  {unreadCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={markAllAsRead}
                      className="text-xs text-muted-foreground"
                    >
                      Mark all read
                    </Button>
                  )}
                </div>
                <ScrollArea className="h-[300px]">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      No notifications
                    </div>
                  ) : (
                    notifications.slice(0, 10).map((notif) => (
                      <div
                        key={notif.id}
                        onClick={() => !notif.read && markAsRead(notif.id)}
                        className={`p-4 border-b cursor-pointer hover:bg-secondary/50 transition-colors ${
                          !notif.read ? "bg-secondary/30" : ""
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`w-2 h-2 rounded-full mt-2 ${
                              notif.type === "success"
                                ? "bg-emerald-500"
                                : notif.type === "warning"
                                ? "bg-amber-500"
                                : notif.type === "error"
                                ? "bg-red-500"
                                : "bg-primary"
                            }`}
                          />
                          <div className="flex-1">
                            <p className="font-medium text-sm">{notif.title}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {notif.message}
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">
                              {formatTimeAgo(notif.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </ScrollArea>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user?.roles?.join(", ") || "User"}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="cursor-pointer"
                  onClick={() => setNotificationSettingsOpen(true)}
                  data-testid="notification-settings-menu-btn"
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Notification Settings
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer">
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer text-destructive focus:text-destructive"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 md:px-8 lg:px-12 py-8">{children}</main>

      {/* What's New slide-in card (bottom-right, dismissed per-user) */}
      <WhatsNewCard />

      {/* Notification Settings Dialog */}
      <Dialog open={notificationSettingsOpen} onOpenChange={setNotificationSettingsOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Notification Settings</DialogTitle>
          </DialogHeader>
          <NotificationSettings 
            user={user} 
            token={token} 
            onUpdate={() => setNotificationSettingsOpen(false)} 
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
